'use client'

import React from 'react'

import { Box, SystemStyleObject, chakra } from '@chakra-ui/react'

import { fontFamilies } from '../../theme/fontFamilies'
import type { ColorScheme } from '../../types'
import { StatusPill, StatusPillVariant } from '../Pill/StatusPill'
import { feedItemStyles } from './FeedItem.styles'

export type FeedItemProps = {
  /** Main activity message */
  message: React.ReactNode
  /**
   * Note slot under the title — a timestamp string ("00:04:48 | 06/15/2026"),
   * a record ID ("CP-157"), or a composed node (icon + count).
   */
  timestamp?: React.ReactNode
  /**
   * Where the timestamp sits. `'below'` (default) renders it as the note line
   * under the title. `'inline-end'` right-aligns it on the title row — for
   * timeline feeds (e.g. the Case Activity Feed) that keep the timestamp on the
   * same line as the actor + action.
   */
  timestampPlacement?: 'below' | 'inline-end'
  /** StatusPill variant controlling the pill color */
  statusVariant?: StatusPillVariant
  /** Label displayed inside the status pill */
  statusLabel?: string
  /** Icon rendered in the left column. The column is omitted entirely when
   * no icon is passed. */
  icon?: React.ReactNode
  /**
   * Avatar for the actor, rendered beside the message. Distinct from `icon`,
   * which says what happened rather than who did it — pass `OperatorAvatar` so
   * AI-Operator rows read differently from a human operator's.
   */
  avatar?: React.ReactNode
  colorScheme?: ColorScheme
  /** Optional content rendered below the timestamp */
  children?: React.ReactNode
  /** Highlights the item as selected in selectable lists. */
  isSelected?: boolean
  /** Optional click handler for selectable feed items. */
  onClick?: () => void
  /** Accessible label for the button. Ignored unless `onClick` is set. */
  ariaLabel?: string
  /**
   * Cross-axis alignment of the icon column against the text block. Feed
   * entries keep 'start' (they can carry children below the note); plain list
   * rows read better centred.
   */
  align?: 'start' | 'center'
  /**
   * Tighter vertical padding, icon column, and no gap between title and note —
   * for dense list rows. Line heights are untouched; those belong to the type
   * scale.
   */
  isCompact?: boolean
  /** Optional slot rendered after the content column (e.g. an ActionMenu
   * trigger). Kept outside the interactive button so nested controls stay
   * valid HTML. */
  trailingAction?: React.ReactNode
  /**
   * Opt-in Design Library text style for the timestamp. Omit to preserve the
   * default Söhne Mono timestamp treatment.
   */
  timestampTextStyle?: SystemStyleObject['textStyle']
  /**
   * Overrides the row's cursor. The row and its inner button both set one, so a
   * cursor applied by a wrapping element is always overridden — pass it here
   * instead (e.g. 'grab' for a row that is dragged rather than clicked).
   */
  cursor?: SystemStyleObject['cursor']
}

export const FeedItem = ({
  message,
  timestamp,
  timestampPlacement = 'below',
  statusVariant,
  statusLabel,
  icon,
  avatar,
  colorScheme = 'dark',
  children,
  isSelected = false,
  onClick,
  ariaLabel,
  align = 'start',
  isCompact = false,
  cursor,
  trailingAction,
  timestampTextStyle,
}: FeedItemProps) => {
  const isInteractive = onClick !== undefined
  const s = feedItemStyles(
    colorScheme,
    isSelected,
    isInteractive,
    align,
    isCompact,
    cursor,
    timestampTextStyle
  )
  const hasStatus = statusVariant !== undefined

  const content = (
    <>
      {icon && (
        <Box {...s.iconCol}>
          <Box {...s.iconFrame}>{icon}</Box>
        </Box>
      )}
      <Box {...s.content}>
        <Box {...s.headerRow}>
          <Box {...s.titleGroup}>
            {avatar && <Box {...s.avatar}>{avatar}</Box>}
            <Box {...s.title}>{message}</Box>
          </Box>
          {hasStatus && (
            <StatusPill
              colorScheme={colorScheme}
              fontFamily={fontFamilies.soehneKraftig}
              fontWeight='500'
              label={statusLabel}
              lineHeight='19px'
              px='8px'
              py='4px'
              variant={statusVariant}
            />
          )}
          {timestamp && timestampPlacement === 'inline-end' && (
            <Box as='span' {...s.timestampInline}>
              {timestamp}
            </Box>
          )}
        </Box>
        {timestamp && timestampPlacement === 'below' && (
          <Box as='span' {...s.timestamp}>
            {timestamp}
          </Box>
        )}
        {children && <Box {...s.children}>{children}</Box>}
      </Box>
    </>
  )

  return (
    <Box {...s.container}>
      {isInteractive ? (
        <chakra.button
          aria-label={ariaLabel}
          aria-pressed={isSelected}
          onClick={onClick}
          type='button'
          {...s.innerButton}
        >
          {content}
        </chakra.button>
      ) : (
        content
      )}
      {trailingAction && <Box {...s.trailingAction}>{trailingAction}</Box>}
    </Box>
  )
}
